import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { sliceKey, reducer, actions } from "./slice";
import { companyAddSaga } from "./saga";
import * as selectors from "./selectors";
import AddCompanyForm from "../../../../forms/AddCompanyForm";
import routes from "../../../../../routes";
import getSchema from "../../../../schemas/addCompanySchema";
import Loader from "../../../../components/Loader";

const AddCompany = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: companyAddSaga });
  const dispatch = useDispatch();
  const history = useHistory();

  // effects
  useEffect(() => {
    dispatch(actions.fetchDropdown(["businessType", "paymentMethod"]));
    dispatch(actions.fetchCountries());
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, history]);

  // selectors
  const dropdowns = useSelector(selectors.selectDropdowns);
  const countries = useSelector(selectors.selectCountries);
  const states = useSelector(selectors.selectStates);
  const cities = useSelector(selectors.selectCities);
  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Creating Company",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      companyName: "",
      businessType: "",
      cin: "",
      abbreviation: "",
      gst: "",
      pan: "",
      registeredAddress: {
        addressLine1: "",
        addressLine2: "",
        district: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        landmark: "",
      },
      /*billingAddress: [
        {
          addressLine1: "",
          addressLine2: "",
          district: "",
          city: "",
          state: "",
          country: "",
          pincode: "",
          landmark: "",
        },
      ],
      pickupAddress: [
        {
          addressLine1: "",
          addressLine2: "",
          district: "",
          city: "",
          state: "",
          country: "",
          pincode: "",
          landmark: "",
        },
      ],*/
      priceVisible: false,
      allowUserPickupAddress: false,
      paymentMethodsAvailable: [],
      admin: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
      },
    },
    resolver: yupResolver(getSchema()),
  });

  const selectedCountry = watch("registeredAddress.country");
  const selectedState = watch("registeredAddress.state");

  useEffect(() => {
    if (selectedCountry)
      dispatch(actions.fetchStates({ countryId: selectedCountry }));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedState)
      dispatch(actions.fetchCities({ stateId: selectedState }));
  }, [selectedState, dispatch]);

  const handleSuccess = () => {
    notification.success({
      message: "Company Got Created",
    });
    history.push(routes.backOffice.companies.list);
  };

  const handleFailure = () => {
    console.log("An error occured");
  };

  const onSubmit = (data) => {
    const payloadData = {
      companyName: data.companyName.trim().split(/ +/).join(" "),
      businessType: data.businessType,
      cin: data.cin,
      gst: data.gst,
      pan: data.pan,
      abbreviation: data.abbreviation,
      registeredAddress: data.registeredAddress,
      //billingAddress: data.billingAddress,
      //pickupAddress: data.pickupAddress,
      priceVisible: data.priceVisible,
      allowUserPickupAddress: data.allowUserPickupAddress,
      paymentMethodsAvailable: data.paymentMethodsAvailable,
    };

    // Include admin data
    payloadData.admin = {
      firstName: data.admin.firstName?.trim(),
      lastName: data.admin.lastName?.trim(),
      email: data.admin.email?.trim().toLowerCase(),
      phone: data.admin.phone?.trim(),
    };
    const payload = {
      onSuccess: handleSuccess,
      data: payloadData,
      onFailure: handleFailure,
    };

    dispatch(actions.addCompany(payload));
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="New Company Account"
        extra={[
          <Button key="1">
            <Link to={routes.backOffice.companies.list}>Cancel</Link>
          </Button>,
          <Button
            disabled={loading}
            loading={loading}
            key="2"
            type="primary"
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {loading ? <Loader /> : ""}
        <AddCompanyForm
          options={{
            businessType: dropdowns.businessType,
            states,
            cities,
            countries,
            paymentMethods: dropdowns.paymentMethod,
          }}
          control={control}
          setValue={setValue}
          watch={watch}
          errors={errors}
          trigger={trigger}
        />
      </div>
    </div>
  );
};

export default AddCompany;
